"use client";

import { useEffect, useState } from "react";

/* Per-viewer lesson progress. Lives in this browser only; storage can be
   blocked (private mode, previews), so every read and write is guarded and
   the pages work without it. */

const KEY = "ll-learn-done";
const EVENT = "ll-learn-progress";

function read(): Set<string> {
  try {
    const raw = window.localStorage.getItem(KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function write(done: Set<string>) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify([...done]));
  } catch {
    /* storage unavailable: progress just isn't remembered */
  }
  window.dispatchEvent(new Event(EVENT));
}

export function lessonKey(track: string, lesson: string) {
  return `${track}/${lesson}`;
}

export function useLearnProgress() {
  const [done, setDone] = useState<Set<string>>(new Set());

  useEffect(() => {
    const sync = () => setDone(read());
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = (key: string) => {
    const next = read();
    if (next.has(key)) next.delete(key);
    else next.add(key);
    write(next);
  };

  return { done, toggle };
}
